import { useCallback, useEffect, useMemo, useState } from "react";
import { getApiBase } from "../apiBase";

const API_BASE = getApiBase();
const IMPORT_COPY = {
  en: {
    loadFailed: "Could not load imported WhatsApp memories.",
    saveFailed: "Could not save this WhatsApp import.",
    discardPrompt: "Discard this imported WhatsApp memory?",
    discardFailed: "Could not discard this WhatsApp import.",
    ungrouped: "Ungrouped (no folder)",
    heading: "WhatsApp import inbox",
    intro:
      "Review memories sent to AfterMe on WhatsApp before they enter your gallery. Tag who they are for, add a message, and decide whether access waits for release.",
    loading: "Loading imported WhatsApp memories…",
    notConfigured: (number) =>
      `WhatsApp imports are not configured on this API yet. Once the inbound number is set up, new photos and videos sent to ${number || "your AfterMe WhatsApp number"} will appear here for review.`,
    empty: "No WhatsApp memories are waiting for review right now.",
    previewUnavailable: "Preview unavailable",
    importPill: "WhatsApp import",
    from: (phone) => `From ${phone}`,
    received: "Received",
    title: "Title",
    message: "Message",
    optional: "(optional)",
    messagePlaceholder: "Add the memory or note that should stay with this file.",
    folder: "Folder",
    tagRecipient: "Tag for recipient",
    notTagged: "Not tagged",
    releaseClosed: "Keep recipient access closed until your release rules are met.",
    saving: "Saving…",
    saveToGallery: "Save to gallery",
    discardImport: "Discard import",
    importedMemoryAlt: "Imported memory",
  },
  fr: {
    loadFailed: "Impossible de charger les souvenirs importés depuis WhatsApp.",
    saveFailed: "Impossible d'enregistrer cette importation WhatsApp.",
    discardPrompt: "Supprimer ce souvenir importé depuis WhatsApp ?",
    discardFailed: "Impossible de supprimer cette importation WhatsApp.",
    ungrouped: "Non classé (aucun espace)",
    heading: "Boîte d’importation WhatsApp",
    intro:
      "Passez en revue les souvenirs envoyés à AfterMe sur WhatsApp avant qu’ils n’entrent dans votre galerie. Indiquez à qui ils sont destinés, ajoutez un message et décidez si l’accès doit attendre la diffusion.",
    loading: "Chargement des souvenirs importés depuis WhatsApp…",
    notConfigured: (number) =>
      `Les importations WhatsApp ne sont pas encore configurées sur cette API. Une fois le numéro entrant configuré, les nouvelles photos et vidéos envoyées à ${number || "votre numéro WhatsApp AfterMe"} apparaîtront ici pour révision.`,
    empty: "Aucun souvenir WhatsApp n’attend de révision pour le moment.",
    previewUnavailable: "Prévisualisation indisponible",
    importPill: "Importation WhatsApp",
    from: (phone) => `De ${phone}`,
    received: "Reçu",
    title: "Titre",
    message: "Message",
    optional: "(facultatif)",
    messagePlaceholder: "Ajoutez le souvenir ou la note qui doit rester avec ce fichier.",
    folder: "Espace",
    tagRecipient: "Associer à un destinataire",
    notTagged: "Aucun destinataire",
    releaseClosed: "Garder l’accès du destinataire fermé jusqu’à ce que vos règles de diffusion soient remplies.",
    saving: "Enregistrement…",
    saveToGallery: "Enregistrer dans la galerie",
    discardImport: "Supprimer l’importation",
    importedMemoryAlt: "Souvenir importé",
  },
};

function isVideoContentType(contentType) {
  return String(contentType || "").toLowerCase().startsWith("video/");
}

function buildDraft(item) {
  return {
    fileName: item.fileName || "",
    memoryMessage: item.memoryMessage || "",
    folderId: item.folderId || "",
    taggedUserId: item.taggedUserId || "",
    taggedDisplayName: item.taggedDisplayName || "",
    recipientAccessAfterRelease: Boolean(item.recipientAccessAfterRelease),
  };
}

export function WhatsAppImportsPanel({
  authToken,
  tagOptions = [],
  folders = [],
  locale = "en",
  onImportsChanged,
}) {
  const copy = IMPORT_COPY[locale === "fr" ? "fr" : "en"];
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [importsEnabled, setImportsEnabled] = useState(false);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState("");
  const [localError, setLocalError] = useState("");
  const [busyId, setBusyId] = useState("");

  const loadImports = useCallback(async ({ silent = false } = {}) => {
    if (!authToken) {
      return;
    }
    if (!silent) {
      setLoading(true);
    }
    setLocalError("");
    try {
      const res = await fetch(`${API_BASE}/api/media/imports`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.loadFailed);
      }
      const nextItems = Array.isArray(data.items) ? data.items : [];
      setItems(nextItems);
      setImportsEnabled(Boolean(data.importsEnabled));
      setBusinessPhoneNumber(typeof data.businessPhoneNumber === "string" ? data.businessPhoneNumber : "");
      setDrafts((prev) => {
        const next = {};
        nextItems.forEach((item) => {
          next[item.mediaId] = prev[item.mediaId] || buildDraft(item);
        });
        return next;
      });
    } catch (error) {
      setLocalError(error.message || copy.loadFailed);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [authToken, copy.loadFailed]);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  useEffect(() => {
    if (!authToken || !importsEnabled) {
      return undefined;
    }
    const id = window.setInterval(() => {
      loadImports({ silent: true });
    }, 20000);
    return () => window.clearInterval(id);
  }, [authToken, importsEnabled, loadImports]);

  const folderOptions = useMemo(
    () => [{ folderId: "", name: copy.ungrouped }, ...folders],
    [folders, copy.ungrouped]
  );

  const setDraftField = (mediaId, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [mediaId]: {
        ...(prev[mediaId] || {}),
        [key]: value,
      },
    }));
  };

  const acceptImport = async (item) => {
    if (!authToken) {
      return;
    }
    const draft = drafts[item.mediaId] || buildDraft(item);
    setBusyId(item.mediaId);
    setLocalError("");
    try {
      const selectedTag = tagOptions.find((option) => option.id === draft.taggedUserId);
      const body = {
        fileName: draft.fileName,
        memoryMessage: draft.memoryMessage,
        folderId: draft.folderId || "",
        taggedUserId: draft.taggedUserId || "",
        taggedDisplayName: selectedTag ? selectedTag.label : draft.taggedDisplayName || "",
        recipientAccessAfterRelease: Boolean(draft.taggedUserId) && draft.recipientAccessAfterRelease,
      };
      const res = await fetch(
        `${API_BASE}/api/media/imports/${encodeURIComponent(item.mediaId)}/accept`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || copy.saveFailed);
      }
      await loadImports();
      if (typeof onImportsChanged === "function") {
        onImportsChanged();
      }
    } catch (error) {
      setLocalError(error.message || copy.saveFailed);
    } finally {
      setBusyId("");
    }
  };

  const rejectImport = async (item) => {
    if (!authToken) {
      return;
    }
    const confirmed = window.confirm(copy.discardPrompt);
    if (!confirmed) {
      return;
    }
    setBusyId(item.mediaId);
    setLocalError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/media/imports/${encodeURIComponent(item.mediaId)}/reject`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || copy.discardFailed);
      }
      await loadImports();
      if (typeof onImportsChanged === "function") {
        onImportsChanged();
      }
    } catch (error) {
      setLocalError(error.message || copy.discardFailed);
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="panel media-gallery-panel media-imports-panel" aria-labelledby="media-imports-heading">
      <div className="panel-head">
        <h2 id="media-imports-heading">{copy.heading}</h2>
        <p>{copy.intro}</p>
      </div>

      {loading ? (
        <p className="helper-text" role="status">
          {copy.loading}
        </p>
      ) : null}

      {!loading && !importsEnabled && items.length === 0 ? (
        <p className="media-notice-banner" role="status">
          {copy.notConfigured(businessPhoneNumber)}
        </p>
      ) : null}

      {localError ? <p className="error-text">{localError}</p> : null}

      {!loading && items.length === 0 && importsEnabled ? (
        <p className="helper-text">
          {copy.empty}
        </p>
      ) : null}

      <div className="whatsapp-import-list">
        {items.map((item) => {
          const draft = drafts[item.mediaId] || buildDraft(item);
          const busy = busyId === item.mediaId;
          return (
            <article key={item.mediaId} className="whatsapp-import-card">
              <div className="whatsapp-import-preview">
                {item.viewUrl && isVideoContentType(item.contentType) ? (
                  <video src={item.viewUrl} controls playsInline muted />
                ) : item.viewUrl ? (
                  <img src={item.viewUrl} alt={draft.fileName || item.fileName || copy.importedMemoryAlt} />
                ) : (
                  <div className="media-thumb media-thumb-fallback">{copy.previewUnavailable}</div>
                )}
              </div>

              <div className="whatsapp-import-form">
                <div className="whatsapp-import-meta">
                  <span className="media-import-pill">{copy.importPill}</span>
                  {item.sourceSenderPhone ? (
                    <span className="helper-text">{copy.from(item.sourceSenderPhone)}</span>
                  ) : null}
                  {item.createdAt ? (
                    <span className="helper-text">
                      {copy.received}{" "}
                      {new Date(item.createdAt).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  ) : null}
                </div>

                <label className="field-label" htmlFor={`wa-import-name-${item.mediaId}`}>
                  {copy.title}
                </label>
                <input
                  id={`wa-import-name-${item.mediaId}`}
                  type="text"
                  value={draft.fileName}
                  onChange={(event) => setDraftField(item.mediaId, "fileName", event.target.value)}
                  disabled={busy}
                  maxLength={300}
                />

                <label className="field-label" htmlFor={`wa-import-message-${item.mediaId}`}>
                  {copy.message} <span className="profile-optional">{copy.optional}</span>
                </label>
                <textarea
                  id={`wa-import-message-${item.mediaId}`}
                  rows={3}
                  value={draft.memoryMessage}
                  onChange={(event) =>
                    setDraftField(item.mediaId, "memoryMessage", event.target.value)
                  }
                  disabled={busy}
                  placeholder={copy.messagePlaceholder}
                />

                <div className="profile-form-grid cols-2">
                  <div>
                    <label className="field-label" htmlFor={`wa-import-folder-${item.mediaId}`}>
                      {copy.folder}
                    </label>
                    <select
                      id={`wa-import-folder-${item.mediaId}`}
                      value={draft.folderId}
                      onChange={(event) =>
                        setDraftField(item.mediaId, "folderId", event.target.value)
                      }
                      disabled={busy}
                    >
                      {folderOptions.map((folder) => (
                        <option key={folder.folderId || "ungrouped"} value={folder.folderId}>
                          {folder.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor={`wa-import-tag-${item.mediaId}`}>
                      {copy.tagRecipient} <span className="profile-optional">{copy.optional}</span>
                    </label>
                    <select
                      id={`wa-import-tag-${item.mediaId}`}
                      value={draft.taggedUserId}
                      onChange={(event) => {
                        const nextTag = event.target.value;
                        setDraftField(item.mediaId, "taggedUserId", nextTag);
                        if (!nextTag) {
                          setDraftField(item.mediaId, "recipientAccessAfterRelease", false);
                        }
                      }}
                      disabled={busy}
                    >
                      <option value="">{copy.notTagged}</option>
                      {tagOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {draft.taggedUserId ? (
                  <label className="media-release-checkbox">
                    <input
                      type="checkbox"
                      checked={draft.recipientAccessAfterRelease}
                      onChange={(event) =>
                        setDraftField(
                          item.mediaId,
                          "recipientAccessAfterRelease",
                          event.target.checked
                        )
                      }
                      disabled={busy}
                    />
                    <span>
                      {copy.releaseClosed}
                    </span>
                  </label>
                ) : null}

                <div className="whatsapp-import-actions">
                  <button type="button" className="small-btn" onClick={() => acceptImport(item)} disabled={busy}>
                    {busy ? copy.saving : copy.saveToGallery}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => rejectImport(item)}
                    disabled={busy}
                  >
                    {copy.discardImport}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
